import type { LatLon } from "@radar-sim/schema";
import { normaliseBearing, projectPosition } from "../geodesy.js";
import type { BehaviourImpl, BehaviourRuntime } from "./types.js";

export interface FishingPatternParams {
  centre: LatLon;
  radiusNm: number;
  speedKnots: number;
}

const SECONDS_PER_HOUR = 3600;

/**
 * Vessel circles a centre point at a fixed radius and speed — a stand-in
 * for trawling/dragging behaviour. Direction is consistent (clockwise as
 * seen from above). Bearing reported is along the tangent.
 */
export const FishingPattern: BehaviourImpl<FishingPatternParams> = {
  name: "fishing_pattern",
  version: "1.0.0",
  init({ params, spawnSimSeconds }): BehaviourRuntime {
    const circumferenceNm = 2 * Math.PI * params.radiusNm;
    const periodHours = circumferenceNm / params.speedKnots;
    const periodSeconds = periodHours * SECONDS_PER_HOUR;
    return {
      poseAt(simSeconds) {
        const elapsed = simSeconds - spawnSimSeconds;
        const phase = (elapsed / periodSeconds) * 2 * Math.PI;
        // Place the vessel on the circle at the current phase.
        const bearingFromCentreDeg = normaliseBearing((phase * 180) / Math.PI);
        const position = projectPosition(
          params.centre,
          bearingFromCentreDeg,
          params.radiusNm,
        );
        // Tangent (clockwise): bearing-from-centre + 90°.
        const cog = normaliseBearing(bearingFromCentreDeg + 90);
        return { position, cog, sog: params.speedKnots };
      },
    };
  },
};
